<template>
    <div class="list-container">
        <h2>Manage Clients</h2>
        <hr />
        <!-- Display Total and Filtered Clients -->
        <div class="d-flex align-items-center mb-3">
            <p class="me-3">Total Clients: {{ totalClients }}</p>
            <p>Filtered Clients: {{ filteredStudents.length }}</p>
        </div>

        <!-- Add Button -->
        <button class="btn btn-sm add-btn" @click="$router.push('/dashboard/clients/add')">➕ Add Client</button>

        <!-- Loading -->
        <div v-if="loading" class="loading-overlay">
            <div class="spinner"></div>
        </div>

        <!-- Filters -->
        <div class="d-flex align-items-center mb-3 flex-wrap">
            <label class="me-2">Filter by Type:</label>
            <select v-model="filterType" class="form-control me-3 mb-2" style="max-width: 200px;">
                <option value="">All</option>
                <option value="IOP">IOP</option>
                <option value="OTP">OTP</option>
                <option value="BOTH">BOTH</option>
            </select>

            <input type="text" class="form-control me-3 mb-2" placeholder="Search by PID" v-model="searchPid"
                style="max-width: 200px;" />
            <input type="text" class="form-control mb-2" placeholder="Search by Name" v-model="searchName"
                style="max-width: 200px;" />

            <!-- Active/Inactive Filter -->
            <label class="me-2">Status:</label>
            <select v-model="filterStatus" class="form-control mb-2" style="max-width: 200px;">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
            </select>

            <label class="me-2">Filter by Medicine:</label>
            <select v-model="filterMedicine" class="form-control mb-2" style="max-width: 200px;">
                <option value="">All</option>
                <option v-for="(medicine, index) in uniqueMedicines" :key="index" :value="medicine">
                    {{ medicine }}
                </option>
            </select>
        </div>

        <!-- Table -->
        <div style="max-height: 650px; overflow-y: auto;">
            <table class="table table-bordered w-100">
                <thead class="table-light">
                    <tr>
                        <th @click="sortBy('pid')" class="text-start">
                            Presentive/Methasoft ID <span v-if="sortKey === 'pid'">{{ sortAsc ? '⬆️' : '⬇️' }}</span>
                        </th>
                        <th @click="sortBy('name')" class="text-start">
                            Name <span v-if="sortKey === 'name'">{{ sortAsc ? '⬆️' : '⬇️' }}</span>
                        </th>
                        <th class="text-start">DOB</th>
                        <th class="text-start">Type</th>
                        <th class="text-start">Group/Counselor</th>
                        <th class="text-start">Medicine</th>
                        <th>Forms</th>
                        <th class="text-start">Active</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="student in filteredStudents" :key="student.id"
                        :class="{ 'text-red': !student.user.is_active }" @click="openModal(student)">
                        <td class="text-start">{{ student.pid }}</td>
                        <td class="text-start">{{ student.user.name }}</td>
                        <td class="text-start">{{ $formatDate(student.dob) }}</td>
                        <td class="text-start">{{ student.student_type }}</td>
                        <td class="text-start">
                            <span v-if="student.student_type === 'OTP'">
                                {{ student.counselor?.user?.name || 'No Counselor' }}
                            </span>
                            <span v-else>
                                {{ student.group?.name || 'Not Assigned' }}
                            </span>
                        </td>
                        <td class="text-start">{{ student.medicine_name }}</td>
                        <td class="text-start">{{ getFormNames(student) }}</td>
                        <td class="text-start">{{ student.user.is_active ? 'Yes' : 'No' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Modal -->
        <div v-if="selectedStudent" class="custom-modal-overlay" @click.self="selectedStudent = null">
            <div class="custom-modal">
                <div class="modal-header d-flex justify-content-between align-items-start">
                    <h5 class="modal-title">Student Details - {{ selectedStudent.user?.name }}</h5>
                    <button type="button" class="btn-close" @click="selectedStudent = null">×</button>
                </div>
                <div class="modal-body">
                    <p><strong>Admission Date:</strong> {{ $formatDate(selectedStudent.admission_date) }}</p>
                    <p><strong>Medicine Name:</strong> {{ selectedStudent.medicine_name || '-' }}</p>
                    <p><strong>Dose:</strong> {{ selectedStudent.dose || '-' }}</p>
                    <div class="d-flex flex-wrap gap-2 mt-3">
                        <button v-if="$canAccess('AgendaIndex')" class="btn btn-sm btn-primary flex-grow-1"
                            @click="$router.push(`/dashboard/clients/edit/${selectedStudent.id}`)">
                            ✏️ Edit
                        </button>
                        <button class="btn btn-sm btn-danger flex-grow-1"
                            @click="toggleStudentStatus(selectedStudent.id)" v-if="selectedStudent.user.is_active">
                            Disable
                        </button>
                        <button class="btn btn-sm btn-success flex-grow-1"
                            @click="toggleStudentStatus(selectedStudent.id)" v-else>
                            Enable
                        </button>
                        <button v-if="$canAccess('student-doctor-orders')" class="btn btn-sm btn-info flex-grow-1"
                            @click="$router.push(`/dashboard/clients/${selectedStudent.id}/doctor-orders`)">
                            🩺 Doctor Orders
                        </button>
                        <button class="btn btn-sm btn-secondary flex-grow-1"
                            @click="$router.push(`/dashboard/phase-determinations/student/${selectedStudent.id}`)">
                            📄 Phase Determination
                        </button>
                        <button v-if="$store.getters.isAdmin" class="btn btn-sm btn-danger flex-grow-1"
                            @click="deleteStudent(selectedStudent.id)">
                            🗑️ Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            students: [],
            loading: false,
            selectedStudent: null,
            filterType: '',
            filterStatus: '',
            searchPid: '',
            searchName: '',
            sortKey: 'pid',
            sortAsc: true,
            studentSignatures: {},
            uniqueMedicines: [],
            filterMedicine: '',
        };
    },
    methods: {
        getFormNames(student) {
            const forms = [];
            if (student.has_phase_determination) forms.push('Phase Determination');
            if (student.has_doctor_order) forms.push('Doctor Order');
            return forms.join(', ');
        },
        async fetchStudents() {
            this.loading = true;
            try {
                const response = await this.$api.get('/students');
                this.students = response.data;
                this.extractMedicines();
            } catch (error) {
                console.error('Error fetching students:', error);
            }
            this.loading = false;
        },
        async toggleStudentStatus(id) {
            if (!confirm("Are you sure you want to change this student's status?")) return;
            try {
                const student = this.students.find((s) => s.id == id);
                await this.$api.patch(`/students/${id}/toggle`);
                student.user.is_active = !student.user.is_active;
            } catch (error) {
                console.error('Error toggling student status:', error);
            }
        },
        extractMedicines() {
            const medicines = this.students
                .map((student) => student.medicine_name)
                .filter(Boolean);
            this.uniqueMedicines = [...new Set(medicines)];
        },
        openModal(student) {
            this.selectedStudent = student;
        },
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },
        async deleteStudent(id) {
            if (!confirm("Are you sure you want to delete this student? This action cannot be undone.")) return;
            try {
                await this.$api.delete(`/students/${id}`);
                this.students = this.students.filter((s) => s.id !== id);
                this.selectedStudent = null;
            } catch (error) {
                console.error('Error deleting student:', error);
                alert('Failed to delete student. Please try again.');
            }
        },
    },
    computed: {
        filteredStudents() {
            let filtered = this.students;

            if (this.filterType) {
                filtered = filtered.filter((s) => s.student_type === this.filterType);
            }

            if (this.filterStatus === 'active') {
                filtered = filtered.filter((s) => s.user.is_active);
            } else if (this.filterStatus === 'inactive') {
                filtered = filtered.filter((s) => !s.user.is_active);
            }

            if (this.filterMedicine) {
                filtered = filtered.filter((s) => s.medicine_name === this.filterMedicine);
            }

            if (this.searchPid) {
                filtered = filtered.filter((s) =>
                    (s.pid || '').toLowerCase().includes(this.searchPid.toLowerCase())
                );
            }

            if (this.searchName) {
                filtered = filtered.filter((s) =>
                    (s.user?.name || '').toLowerCase().includes(this.searchName.toLowerCase())
                );
            }

            return filtered.sort((a, b) => {
                let aVal = this.sortKey === 'name' ? (a.user?.name || '').toLowerCase() : a.pid;
                let bVal = this.sortKey === 'name' ? (b.user?.name || '').toLowerCase() : b.pid;
                if (aVal === undefined || aVal === null) aVal = ''; // Default to empty string if undefined or null
                if (bVal === undefined || bVal === null) bVal = ''; // Default to
                return this.sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            });
        },
        totalClients() {
            return this.students.length;
        },
    },
    mounted() {
        this.fetchStudents();
    },
};
</script>

<style scoped>
.list-container {
    padding: 20px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn {
    min-width: 150px;
    margin-right: 10px;
    margin-bottom: 10px;
}

.signature-preview {
    max-width: 250px;
    border: 1px solid #ccc;
    border-radius: 5px;
}

.text-red {
    color: red;
}

.custom-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1050;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: auto;
    padding: 20px;
}

.custom-modal {
    background: #fff;
    border-radius: 10px;
    max-width: 600px;
    width: 100%;
    padding: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header h5 {
    font-size: 1.25rem;
    margin: 0;
}

.btn-close {
    border: none;
    background: transparent;
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
}

.signature-preview {
    max-width: 100%;
    border: 1px solid #ccc;
    border-radius: 5px;
}

.btn-close {
    background-color: transparent;
    border: none;
    font-size: 1.5rem;
    color: #000;
    /* ensure visibility */
    cursor: pointer;
    margin-left: auto;
    opacity: 1;
    line-height: 1;
}
</style>
